/**
 * Rounded corners for leather outlines (#974, epic #781) — the piece / cutout
 * side of the construction Round sub-tool (#100).
 *
 * The geometry is NOT reimplemented: `applyCornerRounding` in
 * `constructionRounding.ts` already takes a raw pathData string plus a list of
 * `(pathIndex, segmentIndex, radius)` entries, so it serves both. What lives
 * here is the leather half of the same contract construction lines use —
 * `cornerRounding: { sharpPathData, corners }` is the source of truth and the
 * visible outline is re-derived from it on every commit, which is what keeps a
 * radius adjustable and lets a corner go back to sharp.
 *
 * Mirrors `deriveRoundedCorners`'s memoization contract exactly: the input
 * project comes back unchanged when nothing needs an update, so the common
 * no-rounding case costs a walk and nothing else.
 *
 * Stitch seams are deliberately out of scope (#974): a seam follows the outline
 * it was offset from, so rounding belongs to that outline rather than to the
 * row of holes it produced.
 */
import type paper from "paper";
import { applyCornerRounding } from "./constructionRounding";
import type { Cutout, Piece, Project } from "./Project";

/** Re-derive one outline's pathData from its rounding metadata. Returns null
 *  when the object carries no rounding, and falls back to the sharp source
 *  when the rounding produced nothing — better an un-rounded outline than a
 *  vanished part. */
function roundedPathData(
  obj: { cornerRounding?: Piece["cornerRounding"] },
  scope: paper.PaperScope,
): string | null {
  const rounding = obj.cornerRounding;
  if (!rounding || rounding.corners.length === 0) return null;
  const rounded = applyCornerRounding(
    rounding.sharpPathData,
    rounding.corners,
    scope,
  );
  return rounded ?? rounding.sharpPathData;
}

/**
 * Re-derive `shapePathData` / `pathData` for every part and cutout carrying
 * rounding metadata. Runs in the commit pipeline beside `deriveRoundedCorners`.
 */
export function deriveRoundedLeatherCorners(
  project: Project,
  scope: paper.PaperScope,
): Project {
  if (project.pieces.length === 0) return project;
  let changed = false;
  const pieces = project.pieces.map((piece) => {
    let next: Piece = piece;

    const shape = roundedPathData(piece, scope);
    if (shape !== null && shape !== piece.shapePathData) {
      next = { ...next, shapePathData: shape };
      changed = true;
    }

    const cutouts = piece.cutouts ?? [];
    let cutoutsChanged = false;
    const nextCutouts = cutouts.map((cut) => {
      const d = roundedPathData(cut, scope);
      if (d === null || d === cut.pathData) return cut;
      cutoutsChanged = true;
      return { ...cut, pathData: d } as Cutout;
    });
    if (cutoutsChanged) {
      next = { ...next, cutouts: nextCutouts };
      changed = true;
    }
    return next;
  });
  if (!changed) return project;
  return { ...project, pieces };
}

/**
 * Drop rounding metadata after a destructive edit, folding the CURRENT
 * (rounded) geometry in as the new source — the same handoff construction makes
 * when a vertex / handle / curve drag lands on a rounded line.
 *
 * Without this, the next derive pass would rebuild the outline from the stale
 * `sharpPathData` and silently undo the edit the user just made.
 */
export function clearPieceRounding<
  T extends { cornerRounding?: Piece["cornerRounding"] },
>(obj: T): T {
  if (!obj.cornerRounding) return obj;
  const next = { ...obj };
  delete next.cornerRounding;
  return next;
}

/** True when this part has any rounded corner — on its own outline or on one
 *  of its cutouts. Used by the panel to decide whether a "sharpen" affordance
 *  is worth offering. */
export function pieceHasRounding(piece: Piece): boolean {
  if ((piece.cornerRounding?.corners.length ?? 0) > 0) return true;
  return (piece.cutouts ?? []).some(
    (c) => (c.cornerRounding?.corners.length ?? 0) > 0,
  );
}
